import { createMemo, createSignal, For, onCleanup, Show } from 'solid-js';
import { useCraftingResolver } from '../services/craftingResolverContext';
import { useItemName } from '../hooks/useItemName';
import { fromIdentifier } from '../services/itemRefAdapters';
import { useTranslation } from '../hooks/useTranslation';
import type { ItemStack } from '../models/itemStack';
import Text from './Text';
import ItemStackView from './ItemStackView';
import styles from './ItemIdentifierPicker.module.css';

interface ItemIdentifierPickerProps {
    value: string[];
    onChange: (next: string[]) => void;
    placeholder?: string;
        filter?: (def: { id: number; name: string; displayName: string; metadata: number; key: string }) => boolean;
    label?: string;
    dedupe?: boolean;
    maxResults?: number;
    disabled?: boolean;
    // When set, the field holds exactly one item: picking replaces it and the chip has no remove button.
    single?: boolean;
    // When set, blocks with metadata variants (stone, wool, log…) are offered per variant and stored as
    // "minecraft:name:meta" so the consumer can match a specific variation, not just the block id.
    metaAware?: boolean;
    // A value the vanilla catalog cannot name (an NBT item's "id:meta:hash" key) renders from the stack
    // this returns for it; suggestions stay vanilla.
    stackForValue?: (value: string) => ItemStack | undefined;
}

export default function ItemIdentifierPicker(props: ItemIdentifierPickerProps) {
    const { t } = useTranslation();
    const resolver = useCraftingResolver();
    const { itemName, stackName } = useItemName();
    const [query, setQuery] = createSignal('');
    const [debouncedQuery, setDebouncedQuery] = createSignal('');
    const [focused, setFocused] = createSignal(false);

    let searchTimer: number | undefined;
    const onQueryInput = (value: string) => {
        setQuery(value);
        window.clearTimeout(searchTimer);
        searchTimer = window.setTimeout(() => setDebouncedQuery(value), 200);
    };
    onCleanup(() => window.clearTimeout(searchTimer));

    const variantIds = createMemo(() => {
        const r = resolver();
        if (!r || !props.metaAware) return new Set<number>();
        const counts = new Map<number, number>();
        for (const def of r.getAllItems() ?? [])
            counts.set(def.id, (counts.get(def.id) ?? 0) + 1);
        return new Set([...counts.entries()].filter(([, n]) => n > 1).map(([id]) => id));
    });

    const identifierFor = (def: { id: number; name: string; metadata: number }) =>
        props.metaAware && variantIds().has(def.id)
            ? `minecraft:${def.name}:${def.metadata}`
            : `minecraft:${def.name}`;

    const suggestions = createMemo(() => {
        const r = resolver();
        const q = debouncedQuery().trim().toLowerCase();
        if (!r || q.length < 1) return [];
        const all = r.getAllItems() ?? [];
        const selected = new Set(props.value);
        const max = props.maxResults ?? 10;

        type Suggestion = { identifier: string; name: string; metadata: number; key: string };
        const order: number[] = [];
        const groups = new Map<number, Suggestion[]>();
        for (const def of all) {
            if (props.filter && !props.filter(def)) continue;
            const identifier = identifierFor(def);
            if ((props.dedupe ?? true) && selected.has(identifier)) continue;
            if (!r.matchesSearch(def.key, q)) continue;
            let group = groups.get(def.id);
            if (!group) { group = []; groups.set(def.id, group); order.push(def.id); }
            // Dedupe by the stored identifier: name-only when not meta-aware, per-variant when it is.
            if (group.some(x => x.identifier === identifier)) continue;
            group.push({ identifier, name: def.name, metadata: def.metadata, key: def.key });
        }

        // Round-robin across distinct blocks so a block with many metadata variants (wool, stained
        // clay...) can't fill every slot and crowd out an unrelated block matching the same query.
        const out: Suggestion[] = [];
        for (let round = 0; out.length < max; round++) {
            let added = false;
            for (const id of order) {
                const group = groups.get(id)!;
                if (round >= group.length) continue;
                out.push(group[round]);
                added = true;
                if (out.length >= max) break;
            }
            if (!added) break;
        }
        return out;
    });

    const add = (identifier: string) => {
        if ((props.dedupe ?? true) && props.value.includes(identifier)) return;
        props.onChange(props.single ? [identifier] : [...props.value, identifier]);
        setQuery('');
        setDebouncedQuery('');
    };

    const remove = (identifier: string) => {
        props.onChange(props.value.filter(v => v !== identifier));
    };

    const defFor = (identifier: string) => {
        const r = resolver();
        if (!r) return undefined;
        const body = identifier.replace(/^minecraft:/, '');
        const metaMatch = body.match(/^(.+):(\d+)$/);
        const items = r.getAllItems() ?? [];
        if (metaMatch) {
            const name = metaMatch[1];
            const meta = parseInt(metaMatch[2], 10);
            return items.find(i => i.name === name && i.metadata === meta) ?? items.find(i => i.name === name);
        }
        return items.find(i => i.name === body);
    };

    const chipName = (value: string) => {
        const custom = props.stackForValue?.(value);
        if (custom) return stackName(custom);
        const def = defFor(value);
        return def ? itemName(def.key) : value;
    };

    return (
        <div class={styles.picker}>
            <Show when={props.value.length > 0} fallback={
                <div class={styles.empty}>
                    {props.label
                        ? <span>{t("PICKER_EMPTY_LABELED", props.label.toLowerCase())}</span>
                        : <span><Text val="PICKER_EMPTY" /></span>}
                </div>
            }>
                <div class={styles.chips}>
                    <For each={props.value}>
                        {(id) => (
                            <div class={styles.chip}>
                                <ItemStackView stack={props.stackForValue?.(id) ?? fromIdentifier(id, 0, resolver())} size={24} bare />
                                <span class={styles.chipName}>{chipName(id)}</span>
                                <Show when={!props.single}>
                                    <button
                                        type="button"
                                        class={styles.chipRemove}
                                        onClick={() => remove(id)}
                                        disabled={props.disabled}
                                        title={t("REMOVE")}
                                    >
                                        <i class="fa-solid fa-xmark" />
                                    </button>
                                </Show>
                            </div>
                        )}
                    </For>
                </div>
            </Show>

            <div class={styles.searchWrap}>
                <i class={`fa-solid fa-search ${styles.searchIcon}`} />
                <input
                    type="text"
                    class={styles.searchInput}
                    value={query()}
                    onInput={(e) => onQueryInput(e.currentTarget.value)}
                    onFocus={() => setFocused(true)}
                    onBlur={() => setTimeout(() => setFocused(false), 120)}
                    placeholder={props.placeholder ?? t("SEARCH")}
                    disabled={props.disabled}
                />
            </div>

            <Show when={focused() && suggestions().length > 0}>
                <div class={styles.dropdown}>
                    <For each={suggestions()}>
                        {(s) => (
                            <button
                                type="button"
                                class={styles.option}
                                onMouseDown={(e) => { e.preventDefault(); add(s.identifier); }}
                            >
                                <ItemStackView stack={fromIdentifier(s.identifier, 0, resolver())} size={24} bare />
                                <span class={styles.optionName}>
                                    {itemName(s.key)}
                                </span>
                                <span class={styles.optionIdentifier}>{s.identifier}</span>
                            </button>
                        )}
                    </For>
                </div>
            </Show>
        </div>
    );
}
